import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useUserProfile } from './useUserProfile';
import { toast } from 'sonner';

export interface DataImport {
  id: string;
  tenant_id: string;
  file_name: string;
  file_url: string | null;
  status: 'pending' | 'processing' | 'success' | 'error' | 'conflicts';
  rows_total: number | null;
  rows_processed: number | null;
  error_message: string | null;
  imported_by: string | null;
  created_at: string;
  updated_at: string;
  profiles?: {
    full_name: string | null;
  };
}

export function useDataImports() {
  const { user } = useAuth();
  const { tenant } = useUserProfile();
  const queryClient = useQueryClient();

  const { data: imports, isLoading } = useQuery({
    queryKey: ['data-imports', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];
      
      const { data, error } = await supabase
        .from('data_imports')
        .select('*')
        .eq('tenant_id', tenant.id)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      
      // Fetch profile names separately
      const importsWithProfiles = await Promise.all(
        (data || []).map(async (imp) => {
          if (imp.imported_by) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('full_name')
              .eq('user_id', imp.imported_by)
              .single();
            
            return { ...imp, profiles: profile };
          }
          return { ...imp, profiles: null };
        })
      );
      
      return importsWithProfiles as DataImport[];
    },
    enabled: !!tenant?.id,
  });

  const uploadFile = useMutation({
    mutationFn: async (file: File) => {
      if (!tenant?.id || !user?.id) throw new Error('Tenant ou usuário não encontrado');
      
      // Upload file to storage
      const fileName = `${user.id}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('data-imports')
        .upload(fileName, file);
      
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('data-imports')
        .getPublicUrl(fileName);
      
      // Create import record
      const { data: importRecord, error: insertError } = await supabase
        .from('data_imports')
        .insert({
          tenant_id: tenant.id,
          file_name: file.name,
          file_url: publicUrl,
          status: 'pending',
          imported_by: user.id,
        })
        .select()
        .single();
      
      if (insertError) throw insertError;
      
      // Call Edge Function to process import
      const { error: functionError } = await supabase.functions.invoke('process-import', {
        body: { import_id: importRecord.id },
      });
      
      if (functionError) throw functionError;
      
      return importRecord;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-imports'] });
      toast.success('Arquivo enviado! Processamento iniciado.');
    },
    onError: (error: Error) => {
      toast.error('Erro ao enviar arquivo: ' + error.message);
    },
  });

  return {
    imports,
    isLoading,
    uploadFile,
  };
}
